/**
 * Health Score History Component
 * 
 * Shows historical health scores over time with interactive line charts
 */

import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Alert,
  Chip,
  Stack
} from '@mui/material'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts'
import { format, parseISO } from 'date-fns'
import { healthScoreApi, HealthScoreHistory as HealthScoreHistoryData, HealthScoreHistoryEntry } from '../../services/healthScoreApi'

interface HealthScoreHistoryProps {
  fpCode: string
  franchiseName?: string
  initialDays?: number
  initialDomain?: string
  showExceptionCount?: boolean
  height?: number
}

interface ChartDataPoint {
  date: string
  formattedDate: string
  timestamp: number
  overall_score?: number | null
  overall_exceptions?: number
  product_score?: number | null
  product_exceptions?: number
  sale_score?: number | null
  sale_exceptions?: number
  system_score?: number | null
  system_exceptions?: number
}

const domainOptions = [
  { value: '', label: 'All Domains' },
  { value: 'overall', label: 'Overall' },
  { value: 'product', label: 'Products' },
  { value: 'sale', label: 'Sales' },
  { value: 'system', label: 'System' }
]

const dayOptions = [
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' }
]

const domainColors = {
  overall: '#2196f3',
  product: '#4caf50',
  sale: '#ff9800',
  system: '#9c27b0'
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <Card sx={{ p: 1, maxWidth: 300 }}>
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          <Typography variant="subtitle2" gutterBottom>
            {format(parseISO(label), 'MMM dd, yyyy HH:mm')}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Box key={index} display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" sx={{ color: entry.color }}>
                {entry.name}:
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {entry.value !== null ? entry.value.toFixed(1) : 'N/A'}
                {entry.name.includes('exceptions') && entry.value !== null && ` (${entry.value})`}
              </Typography>
            </Box>
          ))}
        </CardContent>
      </Card>
    )
  }
  return null
}

export const HealthScoreHistory: React.FC<HealthScoreHistoryProps> = ({
  fpCode,
  franchiseName,
  initialDays = 30,
  initialDomain = '',
  showExceptionCount = false,
  height = 400
}) => {
  const [days, setDays] = useState<number>(initialDays)
  const [domain, setDomain] = useState<string>(initialDomain)
  const [showExceptions, setShowExceptions] = useState<boolean>(showExceptionCount)
  const [historyData, setHistoryData] = useState<HealthScoreHistoryData | null>(null)
  const [chartData, setChartData] = useState<ChartDataPoint[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchHistoryData = async () => {
      setLoading(true)
      setError(null)
      
      try {
        const history = await healthScoreApi.getHistory(
          fpCode,
          days,
          domain || undefined
        )
        
        setHistoryData(history)
        
        // Convert history data to chart format
        const processedData: ChartDataPoint[] = []
        
        // Collect all unique timestamps
        const allTimestamps = new Set<string>()
        Object.keys(history.history).forEach(domainKey => {
          history.history[domainKey].forEach(entry => {
            allTimestamps.add(entry.date)
          })
        })
        
        // Sort timestamps
        const sortedTimestamps = Array.from(allTimestamps).sort()
        
        // Create data points for each timestamp
        sortedTimestamps.forEach(timestamp => {
          const dataPoint: ChartDataPoint = {
            date: timestamp,
            formattedDate: format(parseISO(timestamp), 'MMM dd HH:mm'),
            timestamp: new Date(timestamp).getTime()
          }
          
          // Add data for each domain
          Object.keys(history.history).forEach(domainKey => {
            const domainEntries = history.history[domainKey]
            const entry = domainEntries.find(e => e.date === timestamp)
            
            if (entry) {
              // Add score
              const scoreKey = `${domainKey}_score` as keyof ChartDataPoint
              ;(dataPoint as any)[scoreKey] = entry.score
              
              // Add exception count if requested
              if (showExceptions) {
                const exceptionKey = `${domainKey}_exceptions` as keyof ChartDataPoint
                ;(dataPoint as any)[exceptionKey] = entry.exception_count
              }
            }
          })
          
          processedData.push(dataPoint)
        })
        
        setChartData(processedData)
        
      } catch (err: any) {
        console.error('Error fetching health score history:', err)
        setError(err.response?.data?.detail || 'Failed to fetch health score history')
      } finally {
        setLoading(false)
      }
    }

    fetchHistoryData()
  }, [fpCode, days, domain, showExceptions])

  const getDomainsToShow = () => {
    if (domain) {
      return [domain]
    }
    return historyData ? Object.keys(historyData.history) : []
  }

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={height}>
            <Stack alignItems="center" spacing={2}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                Loading health score history...
              </Typography>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    )
  }

  if (!historyData || chartData.length === 0) {
    return (
      <Card>
        <CardContent>
          <Alert severity="info">No health score history available for the selected period.</Alert>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardContent>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h6" gutterBottom>
              📈 Health Score History
            </Typography>
            {franchiseName && (
              <Typography variant="body2" color="text.secondary">
                {franchiseName} ({fpCode})
              </Typography>
            )}
          </Box>
          
          <Stack direction="row" spacing={2}>
            {/* Domain Filter */}
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Domain</InputLabel>
              <Select
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                label="Domain"
              >
                {domainOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {/* Days Filter */}
            <ToggleButtonGroup
              value={days}
              exclusive
              onChange={(_, newDays) => newDays && setDays(newDays)}
              size="small"
            >
              {dayOptions.map(option => (
                <ToggleButton key={option.value} value={option.value}>
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            
            {/* Exception Toggle */}
            <ToggleButtonGroup
              value={showExceptions ? 'exceptions' : 'scores'}
              exclusive
              onChange={(_, newValue) => setShowExceptions(newValue === 'exceptions')}
              size="small"
            >
              <ToggleButton value="scores">Scores</ToggleButton>
              <ToggleButton value="exceptions">Exceptions</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Box>
        
        {/* Chart */}
        <Box sx={{ width: '100%', height }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                type="category"
                scale="point"
                tickFormatter={(value) => format(parseISO(value), 'MMM dd')}
                interval="preserveStartEnd"
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis
                domain={showExceptions ? ['dataMin', 'dataMax'] : [0, 100]}
                label={{
                  value: showExceptions ? 'Exception Count' : 'Health Score',
                  angle: -90,
                  position: 'insideLeft'
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              
              {/* Reference lines for score thresholds (only for scores) */}
              {!showExceptions && (
                <>
                  <ReferenceLine y={25} stroke="#4caf50" strokeDasharray="5 5" label="Good" />
                  <ReferenceLine y={50} stroke="#ff9800" strokeDasharray="5 5" label="Watch" />
                  <ReferenceLine y={75} stroke="#f44336" strokeDasharray="5 5" label="Poor" />
                </>
              )}
              
              {/* Lines for each domain */}
              {getDomainsToShow().map(domainKey => {
                const scoreKey = `${domainKey}_${showExceptions ? 'exceptions' : 'score'}`
                const color = domainColors[domainKey as keyof typeof domainColors] || '#666'
                
                return (
                  <Line
                    key={scoreKey}
                    type="monotone"
                    dataKey={scoreKey}
                    stroke={color}
                    strokeWidth={2}
                    dot={{ fill: color, r: 3 }}
                    connectNulls={false}
                    name={`${domainKey} ${showExceptions ? 'exceptions' : 'score'}`}
                  />
                )
              })}
            </LineChart>
          </ResponsiveContainer>
        </Box>
        
        {/* Summary Stats */}
        <Box mt={2} display="flex" gap={2} flexWrap="wrap">
          {getDomainsToShow().map(domainKey => {
            const domainHistory = historyData.history[domainKey] || []
            const validScores = domainHistory
              .map(entry => entry.score)
              .filter(score => score !== null) as number[]
            
            if (validScores.length === 0) return null
            
            const latest = validScores[validScores.length - 1]
            const earliest = validScores[0]
            const change = latest - earliest
            
            return (
              <Chip
                key={domainKey}
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                      {domainKey}:
                    </Typography>
                    <Typography variant="caption" fontWeight="medium">
                      {latest.toFixed(1)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: change > 0 ? '#f44336' : change < 0 ? '#4caf50' : '#9e9e9e'
                      }}
                    >
                      ({change >= 0 ? '+' : ''}{change.toFixed(1)})
                    </Typography>
                  </Box>
                }
                variant="outlined"
                size="small"
              />
            )
          })}
        </Box>
      </CardContent>
    </Card>
  )
}

export default HealthScoreHistory